import { createClient, type SupabaseClient } from '@supabase/supabase-js'

/**
 * Supabase client — single instance for the whole app.
 * Env vars are baked at build time from Vite (VITE_ prefix required).
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined

function isValidUrl(v: string | undefined): boolean {
  if (!v) return false
  try {
    const u = new URL(v)
    return u.protocol === 'https:' || u.protocol === 'http:'
  } catch {
    return false
  }
}

const CONFIGURED = isValidUrl(SUPABASE_URL) && !!SUPABASE_ANON_KEY && SUPABASE_ANON_KEY.length > 20

if (!CONFIGURED && typeof console !== 'undefined') {
  // eslint-disable-next-line no-console
  console.error(
    `[BabyOS] Configuration missing:\n` +
      `  VITE_SUPABASE_URL:      ${SUPABASE_URL ? '(present but invalid URL)' : 'MISSING'}\n` +
      `  VITE_SUPABASE_ANON_KEY: ${SUPABASE_ANON_KEY ? `(present, length ${SUPABASE_ANON_KEY.length})` : 'MISSING'}\n` +
      `Set these in web/.env.local (dev) or your Cloudflare Pages env (prod) then rebuild.`,
  )
}

// Create client with placeholders if unconfigured — the app renders UnconfiguredScreen instead
// of calling any real API. Prevents supabase-js from throwing on module import, which would
// blank the page instead of showing the friendly configuration error.
export const supabase: SupabaseClient = createClient(
  SUPABASE_URL ?? 'http://unconfigured.local',
  SUPABASE_ANON_KEY ?? 'unconfigured_placeholder_key',
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storageKey: 'babyos.auth',
      flowType: 'pkce',
    },
    realtime: {
      params: { eventsPerSecond: 10 },
    },
    db: { schema: 'public' },
    global: {
      headers: { 'x-babyos-client': 'web' },
    },
  },
)

export function supabaseConfigured(): boolean {
  return CONFIGURED
}
